import { SensorPortCommunicationMode } from "../../../portConfigs.js";
import { SensorClusterConfig, SensorElementOutput } from "../types.js";

// ─── HSTI Hydrascout SDI-12 probes ───────────────────────────────────────────
//
// The SDI-12 ASCII decoder yields physical units directly (VWC in %, temperature
// in °C), so no conversion is needed — conversionKey "unity" is used for both
// sensor elements.
//
// SubChannel ordering mirrors the connectedSensors array in the old
// SDI12SensorClusterConfiguration (GetParsedSDI12DataFromByteArray assigns
// subChannel = index):
//   0 .. N-1        VWC, shallowest to deepest
//   N .. 2N-1       temperature, shallowest to deepest

type HydrascoutSdi12Params = {
    sdi12NodeTypeKey: string;
    modelName: string;
    friendlyName: string;
    friendlyShortName: string;
    /** Total probe depth in meters (positive = below ground). */
    depthMeters: number;
    numberSensors: number;
};

function hydrascoutSdi12Cluster({
    sdi12NodeTypeKey,
    modelName,
    friendlyName,
    friendlyShortName,
    depthMeters,
    numberSensors,
}: HydrascoutSdi12Params): SensorClusterConfig<Record<string, never>> {
    const depthsMeters = Array.from({ length: numberSensors }, (_, i) => (depthMeters / numberSensors) * (i + 1));

    const vwcElements: SensorElementOutput<"volumetricWaterContent">[] = depthsMeters.map((depth, i) => ({
        sensorElementConfigKey: "volumetricWaterContent",
        subChannel: i,
        parameters: {},
        position: { depth },
        legalConvertedRange: [0, 100],
    }));

    const tempElements: SensorElementOutput<"soilTemperature">[] = depthsMeters.map((depth, i) => ({
        sensorElementConfigKey: "soilTemperature",
        subChannel: numberSensors + i,
        parameters: {},
        position: { depth },
        legalConvertedRange: [null, null],
    }));

    const requiredSubChannels = Array.from({ length: numberSensors }, (_, i) => i);
    const optionalSubChannels = Array.from({ length: numberSensors }, (_, i) => numberSensors + i);

    return {
        communicationMode: SensorPortCommunicationMode.SDI12,
        sdi12NodeTypeKey,
        requiredSubChannels,
        optionalSubChannels,
        isPermanentlyAttached: false,
        parameterConfigs: {},
        manufacturerName: "HSTI",
        modelName,
        friendlyName,
        friendlyShortName,
        generateSensorElements: () => [...vwcElements, ...tempElements],
    };
}

export const hydrascoutSdi12ClusterConfigs: Record<string, SensorClusterConfig<Record<string, never>>> = {
    hydrascout_sdi12_6in_1sensor: hydrascoutSdi12Cluster({
        sdi12NodeTypeKey: "HSTIhydrascout6Inch1Sensor",
        modelName: `Hydrascout 6" 1-Sensor`,
        friendlyName: `Hydrascout 6" 1-Sensor`,
        friendlyShortName: `HS 6" 1`,
        depthMeters: 0.1524, // 6"
        numberSensors: 1,
    }),
    hydrascout_sdi12_32in_4sensor: hydrascoutSdi12Cluster({
        sdi12NodeTypeKey: "HSTIhydrascout32Inch4Sensor",
        modelName: `Hydrascout 32" 4-Sensor`,
        friendlyName: `Hydrascout 32" 4-Sensor`,
        friendlyShortName: `HS 32" 4`,
        depthMeters: 0.8128, // 32"
        numberSensors: 4,
    }),
    hydrascout_sdi12_32in_4sensor_sandy: hydrascoutSdi12Cluster({
        sdi12NodeTypeKey: "HSTIhydrascout32Inch4SensorSandy",
        modelName: `Hydrascout 32" 4-Sensor (Sandy)`,
        friendlyName: `Hydrascout 32" 4-Sensor (Sandy)`,
        friendlyShortName: `HS 32" 4 Sandy`,
        depthMeters: 0.8128, // 32"
        numberSensors: 4,
    }),
};
